import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import throttle from 'lodash-es/throttle'

import {
  THROTTLE_DELAY_MS,
  TRUNCATION_SYMBOL,
} from './useClampingTextMeasure.constants'
import { computeTruncatedText } from './useClampingTextMeasure.utils'

export function useClampingTextMeasure({
  clampingLinesCount,
  fullText,
  recomputeToken,
  truncationSymbol = TRUNCATION_SYMBOL,
}) {
  const rootRef = useRef(null)
  const measureRef = useRef(null)
  const measureTextRef = useRef(null)
  const measureTailRef = useRef(null)
  const recalcRef = useRef(null)

  const [content, setContent] = useState(fullText)
  const [isTruncated, setIsTruncated] = useState(false)

  function recalc() {
    const measureEl = measureRef.current
    const measureText = measureTextRef.current
    const measureTail = measureTailRef.current

    if (measureEl && measureText && measureTail) {
      const { needsTruncate, renderText } = computeTruncatedText({
        clampingLinesCount,
        fullText,
        measureEl,
        measureTail,
        measureText,
        truncationSymbol,
      })

      setContent(renderText)
      setIsTruncated(needsTruncate)
    }
  }

  // recomputeToken lets the consumer re-measure when the rendered tail changes
  // (e.g. an edit button that only reserves space on hover) without it being
  // tied to fullText/clampingLinesCount.
  useLayoutEffect(() => {
    recalcRef.current = recalc

    recalc()
  }, [clampingLinesCount, fullText, recomputeToken])

  useEffect(() => {
    const node = rootRef.current
    const handleResize = throttle(
      () => recalcRef.current?.(),
      THROTTLE_DELAY_MS
    )

    if (node) {
      const observer = new ResizeObserver(handleResize)

      observer.observe(node)

      return () => {
        observer.disconnect()
        handleResize.cancel()
      }
    }
  }, [])

  return {
    content,
    isTruncated,
    measureRef,
    measureTailRef,
    measureTextRef,
    rootRef,
  }
}
